import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Unstable_Grid2/Grid2";
import TabPanel, { TabPanelProps } from "@mui/lab/TabPanel";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  ListSubheaderProps,
  Paper,
  Typography,
} from "@mui/material";

import { Badge } from "./primitives/Badge";
import { DateTime } from "./primitives/DateTime";
import { HorizontalStack } from "./primitives/Stacks";
import { PatientData } from "../models/patient-data";
import { PlayingIcon } from "@robin/common";
import { useMemo } from "react";

const listSubheaderProps: ListSubheaderProps = {
  sx: {
    bgcolor: "background.default",
    borderBottom: "0.75px solid",
    borderColor: "divider",
    color: "text.primary",
    fontWeight: 500,
  },
};

export const ActivitiesTab: React.FC<TabPanelProps & { data: PatientData }> = ({
  data: { data },
  ...tabPanelProps
}) => {
  const { t } = useTranslation();

  const activities = useMemo(() => {
    return (
      data.positiveActivitiesLog?.map((activity) => ({
        ...activity,
        name: data?.positiveActivites?.find(
          (a) => a.id === activity.positiveActivityId
        )?.name,
      })) || []
    );
  }, [data]);

  if (!data) return;

  const hasActivities = data?.positiveActivitiesLog?.length > 0;

  return (
    <TabPanel {...tabPanelProps}>
      <Typography align="center">
        <Paper sx={{ overflow: "hidden" }} variant="outlined">
          <List
            sx={{ maxHeight: 400, overflow: "auto", scrollbarWidth: "thin" }}
            dense
            disablePadding
            subheader={
              <ListSubheader {...listSubheaderProps}>
                <HorizontalStack alignItems="center" spacing={1}>
                  <span>{t("tabs.activities.activities-label")}</span>
                  <Badge
                    label={data?.positiveActivitiesLog?.length || 0}
                    size="small"
                    variant="outlined"
                  />
                </HorizontalStack>
              </ListSubheader>
            }
          >
            {hasActivities ? (
              activities.map((activity, num) => (
                <ListItem
                  key={activity.id}
                  divider={num < data?.positiveActivitiesLog?.length - 1}
                >
                  <ListItemIcon>
                    <PlayingIcon
                      fontSize="small"
                      sx={{ color: "text.disabled" }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Grid
                        alignItems="center"
                        container
                        spacing={{ xs: 0.5, md: 2 }}
                      >
                        <Grid xs={12} md>
                          <Typography variant="body2">
                            {t(`${activity.name}`)}
                          </Typography>
                        </Grid>
                        <Grid xs={12} md="auto">
                          <Typography
                            alignItems="center"
                            color="text.secondary"
                            display="flex"
                            sx={({ spacing }) => ({ gap: spacing(0.5) })}
                            variant="caption"
                          >
                            <DateTime timestamp={activity.timestamp} />
                          </Typography>
                        </Grid>
                      </Grid>
                    }
                  />
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText
                  primary={t("tabs.activities.activities-no-data")}
                  primaryTypographyProps={{
                    align: "center",
                    color: "text.secondary",
                  }}
                />
              </ListItem>
            )}
          </List>
        </Paper>
      </Typography>
    </TabPanel>
  );
};

export default ActivitiesTab;
